import React, { useEffect, useMemo, useRef, useState } from 'react';
import { socket, emitAck } from './lib/socket.js';
import { clearSession, loadName, loadSession, saveName, saveSession } from './lib/storage.js';
import { HomeScreen } from './components/HomeScreen.jsx';
import { Lobby } from './components/Lobby.jsx';
import { GameScreen } from './components/GameScreen.jsx';

function roomFromUrl() {
  return new URLSearchParams(window.location.search).get('room')?.replace(/\D/g, '').slice(0, 6) || '';
}

export default function App() {
  const [connected, setConnected] = useState(socket.connected);
  const [roomState, setRoomState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const initialCode = useMemo(roomFromUrl, []);
  const rejoinAttempted = useRef(false);
  const roomStateRef = useRef(null);

  useEffect(() => {
    roomStateRef.current = roomState;
  }, [roomState]);

  useEffect(() => {
    const onConnect = () => {
      setConnected(true);
      const currentRoom = roomStateRef.current;
      const code = currentRoom?.code || initialCode;
      const session = code ? loadSession(code) : null;
      if (session?.token && (currentRoom || !rejoinAttempted.current)) {
        rejoinAttempted.current = true;
        emitAck('room:join', { code, name: session.name, token: session.token, asSpectator: session.asSpectator }).catch((joinError) => setError(joinError.message));
      }
    };
    const onDisconnect = () => setConnected(false);
    const onState = (nextState) => setRoomState(nextState);
    const onSessionReplaced = (payload) => {
      const code = roomStateRef.current?.code;
      if (code) clearSession(code);
      setRoomState(null);
      history.replaceState(null, '', window.location.pathname);
      setError(payload?.message || 'Diese Sitzung wurde in einem anderen Tab geöffnet.');
    };
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('room:state', onState);
    socket.on('session:replaced', onSessionReplaced);
    if (socket.connected) onConnect();
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('room:state', onState);
      socket.off('session:replaced', onSessionReplaced);
    };
  }, [initialCode]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(timer);
  }, [toast]);

  async function perform(event, payload = {}) {
    setBusy(true);
    setError('');
    try {
      return await emitAck(event, payload);
    } catch (actionError) {
      setError(actionError.message);
      throw actionError;
    } finally {
      setBusy(false);
    }
  }

  async function createRoom(name) {
    try {
      saveName(name);
      const data = await perform('room:create', { name });
      saveSession(data.code, { token: data.token, name, asSpectator: false });
      history.replaceState(null, '', `?room=${data.code}`);
    } catch {
      // perform() already exposes the user-facing error.
    }
  }

  async function joinRoom(code, name, asSpectator) {
    try {
      saveName(name);
      const existing = loadSession(code);
      const data = await perform('room:join', { code, name, asSpectator, token: existing?.token });
      saveSession(code, { token: data.token, name, asSpectator: Boolean(data.asSpectator) });
      history.replaceState(null, '', `?room=${code}`);
    } catch {
      // perform() already exposes the user-facing error.
    }
  }

  async function leaveRoom() {
    const code = roomState?.code;
    try { await perform('room:leave'); } catch { /* local leave still proceeds */ }
    if (code && (roomState?.status === 'lobby' || roomState?.viewer.role === 'spectator')) clearSession(code);
    setRoomState(null);
    history.replaceState(null, '', window.location.pathname);
  }

  async function action(event, payload = {}) {
    try {
      await perform(event, payload);
      return true;
    } catch {
      return false;
    }
  }

  return (
    <>
      {!roomState && <HomeScreen initialCode={initialCode} initialName={loadName()} connected={connected} busy={busy} onCreate={createRoom} onJoin={joinRoom} />}
      {roomState?.status === 'lobby' && <Lobby state={roomState} busy={busy} onAction={action} onLeave={leaveRoom} />}
      {roomState && roomState.status !== 'lobby' && <GameScreen state={roomState} busy={busy} connected={connected} onAction={action} onLeave={leaveRoom} />}

      {error && <div className="toast toast--error" role="alert"><span>{error}</span><button onClick={() => setError('')}>×</button></div>}
      {toast && <div className="toast" role="status">{toast}</div>}
      {busy && <div className="busy-indicator" aria-label="Wird verarbeitet"><i /><i /><i /></div>}
    </>
  );
}
